import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories, faqItems, features, stats, siteInfo } from '@/mocks/siteData';

const latestArticles = articles.slice(0, 6);

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '91.com',
  url: 'https://91.com',
  description: siteInfo.description,
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '91.com – 官方入口与全功能导航指南',
  url: 'https://91.com',
  description: siteInfo.description,
  inLanguage: 'zh-CN',
};

const webpageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '91.com – 官方入口与全功能导航指南',
  description: siteInfo.description,
  url: 'https://91.com',
  isPartOf: {
    '@type': 'WebSite',
    name: '91.com',
    url: 'https://91.com',
  },
  primaryImageOfPage: {
    '@type': 'ImageObject',
    url: 'https://91.com/og-image.jpg',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function Home() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20network%20connections%20with%20warm%20orange%20and%20teal%20gradient%20nodes%20floating%20in%20a%20soft%20light%20cream%20background%2C%20modern%20minimal%20design%20with%20subtle%20grid%20patterns%2C%20clean%20tech%20aesthetic%2C%20smooth%20flowing%20lines%2C%20professional%20atmosphere%2C%20digital%20network%20visualization&width=1600&height=900&seq=91-hero-bg&orientation=landscape"
                alt="91.com 官方入口导航平台"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            </div>

            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight">
                91.com 官方入口与全功能导航
              </h1>
              <p className="text-base md:text-lg text-white/85 max-w-2xl mx-auto mb-8 leading-relaxed">
                {siteInfo.description}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="https://91.com" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-external-link-line"></i>
                  访问官方入口
                </a>
                <Link to="/category/usage-guide" className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-book-open-line"></i>
                  查看使用指南
                </Link>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="mx-auto px-4 md:px-6 max-w-[1200px]">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 rounded-lg bg-background-100">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="mx-auto px-4 md:px-6 max-w-[1200px]">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">核心功能与服务</h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">全方位了解91.com的功能体系，快速掌握平台使用方法</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {features.map((feature, index) => (
                  <div key={index} className="p-6 rounded-lg bg-background-50 hover:bg-background-50 transition-colors group cursor-pointer">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="mx-auto px-4 md:px-6 max-w-[1200px]">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">内容分类导航</h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">按类别浏览91.com相关文章，快速找到你需要的内容</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {categories.map((cat) => (
                  <Link key={cat.slug} to={`/category/${cat.slug}`} className="flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles Section */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="mx-auto px-4 md:px-6 max-w-[1200px]">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">最新文章</h2>
                  <p className="text-sm text-foreground-600">了解91.com的最新资讯与使用技巧</p>
                </div>
                <Link to="/category/news" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {latestArticles.map((article) => {
                  const cat = categories.find((c) => c.slug === article.category);
                  return (
                    <Link key={article.id} to={`/article/${article.slug}`} className="group block p-5 rounded-lg bg-background-50 hover:bg-background-50 transition-colors cursor-pointer">
                      <div className="flex items-center gap-2 mb-3">
                        {cat && (
                          <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 whitespace-nowrap">
                            {cat.name}
                          </span>
                        )}
                        <span className="text-xs text-foreground-500">{article.date}</span>
                      </div>
                      <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-time-line"></i>
                        <span>{article.readTime} 阅读</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-8 text-center sm:hidden">
                <Link to="/category/news" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  查看全部文章 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-14 md:py-20 bg-background-50">
            <div className="mx-auto px-4 md:px-6 max-w-[900px]">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">常见问题解答</h2>
                <p className="text-sm md:text-base text-foreground-600">关于91.com的常见疑问，这里都有答案</p>
              </div>
              <div className="space-y-3">
                {faqItems.slice(0, 6).map((item, index) => (
                  <details key={index} className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer">
                    <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 list-none">
                      <span>{item.question}</span>
                      <i className="ri-add-line text-foreground-500 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-500 hidden group-open:block"></i>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </div>
                  </details>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link to="/category/faq" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  查看更多常见问题 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-16 md:py-24 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20orange%20and%20teal%20abstract%20flowing%20gradient%20waves%20on%20a%20soft%20cream%20background%2C%20smooth%20curves%20with%20floating%20luminous%20dots%2C%20modern%20minimal%20tech%20atmosphere%2C%20professional%20clean%20design%2C%20digital%20connection%20theme&width=1600&height=600&seq=91-cta-bg&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-accent-600/85"></div>
            </div>
            <div className="relative z-10 mx-auto px-4 md:px-6 max-w-[700px] text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">立即访问 91.com 官方平台</h2>
              <p className="text-sm md:text-base text-white/85 mb-8 leading-relaxed">
                安全、稳定、高效的综合性网络平台，数百万用户的选择。立即访问，开启你的91.com之旅。
              </p>
              <a href="https://91.com" target="_blank" rel="nofollow noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-primary-600 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-external-link-line"></i>
                进入 91.com 官方入口
              </a>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}